import { CROSSING_KINDS, SEAM_STATES, canonicalPlace, sortedCrossings, sortedSeams } from "./layout.ts";
import type { CrossingKind, MapAtmosphere, MapDocument, MapGroup, MapHost, MapLight, MapPlace, MapShare, Seam, SeamCrossing, SeamState, WorldDocument, WorldGrid } from "./types.ts";
import { asArray, asBoolean, asInteger, asNumber, asRecord, asString } from "./validate.ts";

/**
 * The layout in the saved document (ADR-109 decision 10, ADR-122 decision 1.5): how the world's `grid`,
 * `mapGroups` and `seams` and a map's `place` (with its depth), `host`, `share`, `capacity`, `atmosphere`,
 * `mapGroup` and `draft` are read and written. The layout's are schema 10's names and the room fields
 * (a place's depth, `host`, `share`, `capacity`, `atmosphere`) schema 11's: a document of an earlier schema that
 * carries one is refused by name, as every schema's new names are. The rules across fields
 * (the lattice, overlaps, shared edges) are `validateDocument`'s, which the reader runs once the whole world is
 * read.
 */

/**
 * The first schema that holds the layout.
 */
export const LAYOUT_SCHEMA = 10;

/**
 * The first schema that holds where a map sits and who shares it: a place's depth, a room's host, its sharing,
 * capacity and atmosphere (ADR-122 decision 1.5), so a build that reads only `LAYOUT_SCHEMA` refuses a room by
 * its schema and never saves it as a plain map.
 */
export const ROOM_SCHEMA = 11;

/**
 * The first schema whose atmosphere may carry `dim`. A build that wrote an earlier schema refuses the key by
 * name, so a world holding one must not claim a schema that build reads, and this build refuses it below 14.
 */
export const DIM_SCHEMA = 14;

/**
 * The world's names the layout adds.
 */
const WORLD_NAMES = ["grid", "mapGroups", "seams"] as const;

/**
 * A map's names the layout adds.
 */
const MAP_NAMES = ["place", "mapGroup", "draft"] as const;

/**
 * A map's names the room fields add.
 */
const ROOM_NAMES = ["host", "share", "capacity", "atmosphere"] as const;

/**
 * Every way a map may be shared, in the order the reader names them.
 */
const SHARES: readonly MapShare[] = ["open", "party", "solo", "owner"];

/**
 * Every light a map may have, in the order the reader names them.
 */
const LIGHTS: readonly MapLight[] = ["sky", "indoor"];

/**
 * Refuses a layout name in a document of a schema before the layout.
 *
 * @param record The world's or a map's fields.
 * @param names The names to look for.
 * @param schemaVersion The schema the document was stored at.
 * @param what What the record is, for the message: "document" or "map".
 * @param path The record's path, empty for the world.
 * @param since The first schema that holds the names, `LAYOUT_SCHEMA` by default.
 * @throws Error naming the field when the schema is before `since` and the record holds it.
 */
function refuseEarly(record: Record<string, unknown>, names: readonly string[], schemaVersion: number, what: string, path: string, since = LAYOUT_SCHEMA): void {
  if (schemaVersion >= since) {
    return;
  }
  for (const name of names) {
    if (record[name] !== undefined) {
      throw new Error(`${path === "" ? "" : `${path}.`}${name} is not a field of a schema ${schemaVersion} ${what}; it came in schema ${since}`);
    }
  }
}

/**
 * Asserts a record has no key beyond the given ones.
 *
 * @param record The record.
 * @param keys The keys it may hold.
 * @param path Its path.
 * @throws Error naming the first other key.
 */
function onlyKeys(record: Record<string, unknown>, keys: readonly string[], path: string): void {
  const other = Object.keys(record).find((key) => !keys.includes(key));
  if (other !== undefined) {
    throw new Error(`${path}.${other} is not a field`);
  }
}

/**
 * Reads one of a closed list of words.
 *
 * @param value The value.
 * @param words The words.
 * @param path The path.
 * @returns The word.
 * @throws Error naming the path and the words when the value is not one of them.
 */
function oneOf<T extends string>(value: unknown, words: readonly T[], path: string): T {
  if (typeof value !== "string" || !(words as readonly string[]).includes(value)) {
    throw new Error(`${path} must be one of ${words.map((word) => JSON.stringify(word)).join(", ")}`);
  }
  return value as T;
}

/**
 * Reads a place.
 *
 * @param value The value.
 * @param path The path.
 * @param schemaVersion The schema the document was stored at; a depth is refused before `ROOM_SCHEMA`.
 * @returns The place, a depth of 0 read as none.
 * @throws Error naming the path when it is not `{ x, y, depth? }` of integers, or holds a depth before
 *   `ROOM_SCHEMA`.
 */
export function readPlace(value: unknown, path: string, schemaVersion = ROOM_SCHEMA): MapPlace {
  const record = asRecord(value, path);
  onlyKeys(record, ["x", "y", "depth"], path);
  refuseEarly(record, ["depth"], schemaVersion, "map", path, ROOM_SCHEMA);
  const depth = record["depth"] === undefined ? 0 : asInteger(record["depth"], `${path}.depth`);
  return { x: asInteger(record["x"], `${path}.x`), y: asInteger(record["y"], `${path}.y`), ...(depth === 0 ? {} : { depth }) };
}

/**
 * Reads a room's host.
 *
 * @param value The value.
 * @param path The path.
 * @returns The host.
 * @throws Error naming the path when it is not `{ map, arrival }` of strings.
 */
export function readHost(value: unknown, path: string): MapHost {
  const record = asRecord(value, path);
  onlyKeys(record, ["map", "arrival"], path);
  return { map: asString(record["map"], `${path}.map`), arrival: asString(record["arrival"], `${path}.arrival`) };
}

/**
 * Reads an atmosphere.
 *
 * @param value The value.
 * @param path The path.
 * @param schemaVersion The schema the document was stored at; a `dim` is refused before `DIM_SCHEMA`.
 * @returns The atmosphere, each field present only when set.
 * @throws Error naming the path when a field is malformed or unknown, or `dim` is present below `DIM_SCHEMA`.
 */
export function readAtmosphere(value: unknown, path: string, schemaVersion = DIM_SCHEMA): MapAtmosphere {
  const record = asRecord(value, path);
  onlyKeys(record, ["music", "ambience", "light", "dim"], path);
  refuseEarly(record, ["dim"], schemaVersion, "atmosphere", path, DIM_SCHEMA);
  return {
    ...(record["music"] === undefined ? {} : { music: asString(record["music"], `${path}.music`) }),
    ...(record["ambience"] === undefined ? {} : { ambience: asString(record["ambience"], `${path}.ambience`) }),
    ...(record["light"] === undefined ? {} : { light: oneOf(record["light"], LIGHTS, `${path}.light`) }),
    ...(record["dim"] === undefined ? {} : { dim: asNumber(record["dim"], `${path}.dim`) }),
  };
}

/**
 * Reads the grid.
 *
 * @param value The value.
 * @param path The path.
 * @returns The grid.
 * @throws Error naming the path when it is not `{ cell, x, y }` of integers.
 */
export function readGrid(value: unknown, path: string): WorldGrid {
  const record = asRecord(value, path);
  onlyKeys(record, ["cell", "x", "y"], path);
  return { cell: asInteger(record["cell"], `${path}.cell`), x: asInteger(record["x"], `${path}.x`), y: asInteger(record["y"], `${path}.y`) };
}

/**
 * Reads a map group.
 *
 * @param value The value.
 * @param path The path.
 * @returns The group.
 * @throws Error naming the path when it is not `{ id, name, colour }` of strings.
 */
export function readMapGroup(value: unknown, path: string): MapGroup {
  const record = asRecord(value, path);
  onlyKeys(record, ["id", "name", "colour"], path);
  return { id: asString(record["id"], `${path}.id`), name: asString(record["name"], `${path}.name`), colour: asString(record["colour"], `${path}.colour`) };
}

/**
 * Reads a crossing.
 *
 * @param value The value.
 * @param path The path.
 * @returns The crossing, `fixed` kept only when true.
 * @throws Error naming the path when a field is malformed or unknown.
 */
export function readCrossing(value: unknown, path: string): SeamCrossing {
  const record = asRecord(value, path);
  onlyKeys(record, ["name", "kind", "from", "to", "fixed"], path);
  const kind: CrossingKind = oneOf(record["kind"], CROSSING_KINDS, `${path}.kind`);
  const fixed = record["fixed"] === undefined ? false : asBoolean(record["fixed"], `${path}.fixed`);
  return {
    name: asString(record["name"], `${path}.name`),
    kind,
    ...(record["from"] === undefined ? {} : { from: asInteger(record["from"], `${path}.from`) }),
    ...(record["to"] === undefined ? {} : { to: asInteger(record["to"], `${path}.to`) }),
    ...(fixed ? { fixed: true as const } : {}),
  };
}

/**
 * Reads a seam.
 *
 * @param value The value.
 * @param path The path.
 * @returns The seam, its crossings in name order.
 * @throws Error naming the path when a field is malformed or unknown.
 */
export function readSeam(value: unknown, path: string): Seam {
  const record = asRecord(value, path);
  onlyKeys(record, ["a", "b", "state", "barrier", "crossings"], path);
  const state: SeamState = oneOf(record["state"], SEAM_STATES, `${path}.state`);
  return {
    a: asString(record["a"], `${path}.a`),
    b: asString(record["b"], `${path}.b`),
    state,
    ...(record["barrier"] === undefined ? {} : { barrier: asString(record["barrier"], `${path}.barrier`) }),
    crossings: sortedCrossings(asArray(record["crossings"], `${path}.crossings`).map((item, i) => readCrossing(item, `${path}.crossings[${i}]`))),
  };
}

/**
 * Reads the world's layout fields.
 *
 * @param world The world's fields.
 * @param schemaVersion The schema the document was stored at.
 * @returns The fields present, to spread into the world.
 * @throws Error naming the field when one is malformed, or present below `LAYOUT_SCHEMA`.
 */
export function readWorldLayout(world: Record<string, unknown>, schemaVersion: number): Pick<WorldDocument, "grid" | "mapGroups" | "seams"> {
  refuseEarly(world, WORLD_NAMES, schemaVersion, "document", "");
  const groups = world["mapGroups"] === undefined ? [] : asArray(world["mapGroups"], "mapGroups").map((item, i) => readMapGroup(item, `mapGroups[${i}]`));
  const seams = world["seams"] === undefined ? [] : asArray(world["seams"], "seams").map((item, i) => readSeam(item, `seams[${i}]`));
  return {
    ...(world["grid"] === undefined ? {} : { grid: readGrid(world["grid"], "grid") }),
    ...(groups.length === 0 ? {} : { mapGroups: groups }),
    ...(seams.length === 0 ? {} : { seams: sortedSeams(seams) }),
  };
}

/**
 * Reads a map's layout fields.
 *
 * @param record The map's fields.
 * @param schemaVersion The schema the document was stored at.
 * @param path The map's path.
 * @returns The fields present, to spread into the map; a draft saved as false is ready, a share saved as open
 *   is everyone's, and an empty atmosphere is none, each writing nothing.
 * @throws Error naming the field when one is malformed, or present below `LAYOUT_SCHEMA` (the room fields below
 *   `ROOM_SCHEMA`).
 */
export function readMapLayout(record: Record<string, unknown>, schemaVersion: number, path: string): Pick<MapDocument, "place" | "host" | "share" | "capacity" | "atmosphere" | "mapGroup" | "draft"> {
  refuseEarly(record, MAP_NAMES, schemaVersion, "map", path);
  refuseEarly(record, ROOM_NAMES, schemaVersion, "map", path, ROOM_SCHEMA);
  const draft = record["draft"] === undefined ? false : asBoolean(record["draft"], `${path}.draft`);
  const share = record["share"] === undefined ? "open" : oneOf(record["share"], SHARES, `${path}.share`);
  const atmosphere = record["atmosphere"] === undefined ? {} : readAtmosphere(record["atmosphere"], `${path}.atmosphere`, schemaVersion);
  return {
    ...(record["place"] === undefined ? {} : { place: readPlace(record["place"], `${path}.place`, schemaVersion) }),
    ...(record["host"] === undefined ? {} : { host: readHost(record["host"], `${path}.host`) }),
    ...(share === "open" ? {} : { share }),
    ...(record["capacity"] === undefined ? {} : { capacity: asInteger(record["capacity"], `${path}.capacity`) }),
    ...(Object.keys(atmosphere).length === 0 ? {} : { atmosphere }),
    ...(record["mapGroup"] === undefined ? {} : { mapGroup: asString(record["mapGroup"], `${path}.mapGroup`) }),
    ...(draft ? { draft: true as const } : {}),
  };
}

/**
 * A crossing's plain shape, in fixed key order.
 *
 * @param crossing The crossing.
 * @returns The plain object.
 */
function plainCrossing(crossing: SeamCrossing): Record<string, unknown> {
  return {
    name: crossing.name,
    kind: crossing.kind,
    ...(crossing.from === undefined ? {} : { from: crossing.from }),
    ...(crossing.to === undefined ? {} : { to: crossing.to }),
    ...(crossing.fixed === true ? { fixed: true } : {}),
  };
}

/**
 * The world's layout fields as written, in fixed key order: the grid and the groups before the maps.
 *
 * @param world The world.
 * @returns The fields to spread before `maps`.
 */
export function plainWorldLayout(world: WorldDocument): Record<string, unknown> {
  return {
    ...(world.grid === undefined ? {} : { grid: { cell: world.grid.cell, x: world.grid.x, y: world.grid.y } }),
    ...(world.mapGroups === undefined || world.mapGroups.length === 0 ? {} : { mapGroups: world.mapGroups.map((g) => ({ id: g.id, name: g.name, colour: g.colour })) }),
  };
}

/**
 * A list of seams as written, in key order, each crossing in name order and each key in a fixed order: the one text
 * a document, a manifest and a version label all give for the same seams.
 *
 * @param seams The seams.
 * @returns The plain seams.
 */
export function plainSeamList(seams: readonly Seam[]): Record<string, unknown>[] {
  return sortedSeams(seams).map((seam) => ({
    a: seam.a,
    b: seam.b,
    state: seam.state,
    ...(seam.barrier === undefined ? {} : { barrier: seam.barrier }),
    crossings: sortedCrossings(seam.crossings).map(plainCrossing),
  }));
}

/**
 * The world's seams as written, in key order, each crossing in name order.
 *
 * @param world The world.
 * @returns The field to spread after `maps`.
 */
export function plainSeams(world: WorldDocument): Record<string, unknown> {
  if (world.seams === undefined || world.seams.length === 0) {
    return {};
  }
  return { seams: plainSeamList(world.seams) };
}

/**
 * A map's layout fields as written, in fixed key order.
 *
 * @param map The map.
 * @returns The fields to spread after `seed`.
 */
export function plainMapLayout(map: MapDocument): Record<string, unknown> {
  const place = canonicalPlace(map.place);
  const atmosphere = map.atmosphere ?? {};
  return {
    ...(place === undefined ? {} : { place }),
    ...(map.host === undefined ? {} : { host: { map: map.host.map, arrival: map.host.arrival } }),
    ...(map.share === undefined ? {} : { share: map.share }),
    ...(map.capacity === undefined ? {} : { capacity: map.capacity }),
    ...(Object.keys(atmosphere).length === 0 ? {} : { atmosphere: { ...(atmosphere.music === undefined ? {} : { music: atmosphere.music }), ...(atmosphere.ambience === undefined ? {} : { ambience: atmosphere.ambience }), ...(atmosphere.light === undefined ? {} : { light: atmosphere.light }), ...(atmosphere.dim === undefined ? {} : { dim: atmosphere.dim }) } }),
    ...(map.mapGroup === undefined ? {} : { mapGroup: map.mapGroup }),
    ...(map.draft === true ? { draft: true } : {}),
  };
}
